//------------------------------------------------------------------------
// YouTube video player tracking and dynamic loading
// https://developers.google.com/youtube/iframe_api_reference
//
// How to track player events in GTM
// https://www.simoahava.com/analytics/the-youtube-video-trigger-in-google-tag-manager/
//
// Requires YouTube Data API key to add proper “title” for accessiblity
// https://developers.google.com/youtube/v3/getting-started
//
// Video players can be built dynamically using data attributes:
// <div data-youtube="FuYW3GdPhsc">
//   <a href="https://www.youtube.com/watch?v=FuYW3GdPhsc">Watch video on YouTube</a>
// </div>
//------------------------------------------------------------------------

/* global YT XDomainRequest */
"use strict";
import loadJS from "fg-loadjs";
const publicKey = window.APP && window.APP.YOUTUBE_PUBLIC_KEY;

class YoutubePlayer {
  constructor(el) {
    if (!publicKey) {
      console.warn("No YouTube API public key provided, can’t get video title");
      return false;
    }

    this.el = el;

    // Get YouTube video ID
    this.videoID = this.el.getAttribute("data-youtube");

    if (!this.videoID) {
      console.warn("Missing YouTube ID");
      return false;
    }

    // Find elements that need to be updated with video data
    this.titleEl = document.querySelectorAll(
      `[data-youtube-title="${this.videoID}"]`
    );
    this.timeEl = document.querySelectorAll(
      `[data-youtube-time="${this.videoID}"]`
    );
    this.imageEl = document.querySelectorAll(
      `[data-youtube-image="${this.videoID}"]`
    );

    // Get ID of element where video should be injected
    this.playerID = this.el.getAttribute("data-youtube-player");

    if (!this.playerID) {
      console.warn("Missing ID of YouTube video wrapper");
      return false;
    }

    // Find parent element selector (optional)
    // This alows us to add a class when the video has loaded.
    this.parentSelector = this.el.getAttribute("data-youtube-parent");

    if (this.parentSelector) {
      this.parentEl = this.el.closest(this.parentSelector);
    }

    // Get playerVars from optional data attributes if present
    this.collectVars();

    // Get video info (e.g. title, time)
    this.lookupVideoData();

    // Click event
    this.el.addEventListener("click", evt => {
      evt.preventDefault();

      // Create the video player if not already present
      if (!this.player) {
        this.buildPlayer();
      } else {
        // Toggle the playback
        if (this.player.getPlayerState() === 1) {
          this.player.pauseVideo();
        } else {
          this.player.playVideo();
        }
      }
    });
  }

  // Collect player vars from data attributes (if present)
  collectVars() {
    this.playerVars = {};
    this.playerVars.autohide =
      this.el.getAttribute("data-youtube-autohide") || 1;
    this.playerVars.autoplay =
      this.el.getAttribute("data-youtube-autoplay") || 1;
    // controls=2 does not work when enablejsapi=1
    this.playerVars.controls =
      this.el.getAttribute("data-youtube-controls") || 1;
    this.playerVars.iv_load_policy =
      this.el.getAttribute("data-youtube-iv_load_policy") || 3;
    this.playerVars.showinfo =
      this.el.getAttribute("data-youtube-showinfo") || 0;
    this.playerVars.rel = this.el.getAttribute("data-youtube-rel") || 0;
    this.playerVars.wmode = "opaque";
    // console.log('this.playerVars', this.playerVars);
  }

  // Plain JS AJAX request
  // https://plainjs.com/javascript/ajax/making-cors-ajax-get-requests-54/
  getCORS(url, success, error) {
    var xhr = new XMLHttpRequest();
    if (!("withCredentials" in xhr)) xhr = new XDomainRequest(); // fix IE8/9
    xhr.open("GET", url);
    xhr.onload = success;
    xhr.onerror = error;
    xhr.send();
    return xhr;
  }

  // Get video metadata using YouTube gdata API
  // https://developers.google.com/youtube/v3/getting-started
  getVideoData(callback) {
    let url = `https://www.googleapis.com/youtube/v3/videos?id=${this.videoID}&key=${publicKey}&part=snippet,contentDetails`;

    this.getCORS(
      url,
      request => {
        let response = request.currentTarget.response || request.target.responseText;
        callback(JSON.parse(response));
      },
      function() {
        this.videoTitle = "Title not found";
      }
    );
  }

  lookupVideoData() {
    this.videoTitle = this.el.getAttribute("data-youtube-title") || "";

    this.getVideoData(data => {
      if ("items" in data && data.items.length) {
        let video = data.items[0];
        this.updateDOM({
          duration: video.contentDetails.duration,
          title: video.snippet.title,
          thumbnails: video.snippet.thumbnails
        });
      } else {
        console.warn("Can’t get YouTube data for video ID " + this.videoID);
      }
    });
  }

  // Convert YouTube’s weird duration string to something human readable (e.g. PT2H34M25S => 2:34:25)
  // Based on https://gist.github.com/Fauntleroy/5167736#gistcomment-2094514
  convertYouTubeDuration(duration) {
    let pattern = /([0-9]*H)?([0-9]*M)?([0-9]*S)?$/;
    let extracted = pattern.exec(duration);
    let hours = parseInt(extracted[1], 10) || 0;
    let minutes = parseInt(extracted[2], 10) || 0;
    let seconds = parseInt(extracted[3], 10) || 0;

    // Pad single-digit seconds with zero
    seconds = seconds < 10 ? `0${seconds}` : seconds;

    if (hours + minutes === 0) {
      return `0:${seconds}`;
    }

    if (hours === 0) {
      return `${minutes}:${seconds}`;
    }

    // Pad single-digit minutes with zero
    minutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours}:${minutes}:${seconds}`;
  }

  updateDOM(data) {
    if (data.title) {
      this.titleEl.forEach(el => {
        el.textContent = data.title;

        // find the parent element with class 'VideoPromo-content-text'
        const contentTextEl = el.closest('.VideoPromo-content-text');

        // add class 'is-loaded' if the element exists
        if (contentTextEl) {
          contentTextEl.classList.add('is-loaded');
        }
      });
    }

    if (data.duration) {
      this.timeEl.forEach(el => {
        el.textContent = this.convertYouTubeDuration(data.duration);
      });
    }

    if (data.thumbnails) {
      this.checkPosterImages(data.thumbnails);
    }
  }

  // Check for any poster image sizes that do not exist and remove from “srcset”
  // (YouTube doesn’t generate high-res poster images for low-quality videos)
  checkPosterImages(thumbnails) {
    // Save available YouTube images to an array
    let youtubeImageUrls = [];

    for (const key in thumbnails) {
      youtubeImageUrls.push(thumbnails[key].url);
    }

    // Cross-reference with the sources in the “srcset” attribute
    this.imageEl.forEach(image => {
      // Convert “srcset” values to an array
      let sources = image.getAttribute("srcset").split(",");
      sources = sources.map(d => d.trim());
      // Create new placeholder array to avoid altering the original during the loop below
      let finalSources = [];

      // Check each “srcset” URL against YouTube array
      for (var i=0, len=sources.length; i<len; i++) {
        let imgUrl = sources[i].split(" ")[0];

        // Only keep the image sources YouTube has
        if (youtubeImageUrls.indexOf(imgUrl) > -1) {
          finalSources.push(sources[i]);
        }
      }

      // Update “srcset” attribute
      image.setAttribute("srcset", finalSources.join(","));
    });
  }

  buildPlayer() {
    // Replace the placeholder element with the iframe
    // Add optional class to parent element
    if (this.parentEl) {
      this.parentEl.classList.add("is-loading");
    }
    this.player = new YT.Player(this.playerID, {
      videoId: this.videoID,
      playerVars: this.playerVars,
      events: {
        onReady: () => {
          // Add optional class to parent element
          if (this.parentEl) {
            this.parentEl.classList.add("is-ready");
          }

          // Focus the iframe
          document.getElementById(this.playerID).focus();
        }
      }
    });
  }
}

// Init video players
const initPlayers = els => {
  // console.log('Init YT players');
  els.forEach(el => {
    new YoutubePlayer(el);
  });
};

const init = () => {
  // Find video links
  let videos = document.querySelectorAll("[data-youtube]");

  if (publicKey && videos.length) {
    // Check if YouTube JS API has already been loaded
    // https://developers.google.com/youtube/iframe_api_reference
    if ("YT" in window) {
      initPlayers(videos);
    } else {
      // Set custom “onYouTubeIframeAPIReady” callback that will fire
      // once the YouTube API script has downloaded and run.
      window.onYouTubeIframeAPIReady = function() {
        // console.log("YT API ready");
        initPlayers(videos);
      };

      // Async load the YouTube JS API script
      loadJS("https://www.youtube.com/iframe_api", function() {
        // Note: The YouTube script automatically calls onYouTubeIframeAPIReady()
        //       when ready so we don’t need to do anything here.
        // console.log("YT API loaded");
      });
    }
  }
};

// Init on initial page load
init();
